import React from 'react';
import { Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';
import {
  useGetActivityLogsQuery,
  ActivityLogItem,
} from '@/store/services/configLogsService';
import IndividualLogItem from './IndividualLogItem';
import InfoText from '@/components/atoms/InfoText';
import { cn } from '@/lib/helperClass';

export interface ActivityLogsListProps {
  type?: string;
  id?: string;
  className?: string;
  hideTitle?: boolean;
  showEmptyState?: boolean;
}

export const ActivityLogsList: React.FC<ActivityLogsListProps> = ({
  type = 'REGISTRATION',
  id = 'b31692d4-8647-4698-9acf-38df9170bc05',
  className,
  hideTitle = false,
  showEmptyState = false,
}) => {
  const { t } = useTranslation();

  const { data, isLoading } = useGetActivityLogsQuery(
    { type, id },
    { skip: !type || !id, refetchOnMountOrArgChange: true }
  );


  const logsList: ActivityLogItem[] = React.useMemo(() => {
    if (!data) return [];
    return data?.payload || [];
  }, [data]);

  if (isLoading) {
    return (
      <div
        className={cn('flex flex-col gap-4 p-4', className)}
        data-testid="activity-logs-list-loading"
      >
        <Skeleton active paragraph={{ rows: 2 }} />
        <Skeleton active paragraph={{ rows: 2 }} />
        <Skeleton active paragraph={{ rows: 2 }} />
      </div>
    );
  }

  if (!logsList?.length) {
    if (showEmptyState) {
      return (
        <div
          className={cn(
            'flex flex-col items-center justify-center p-8 text-center',
            className
          )}
          data-testid="activity-logs-list-empty"
        >
          <InfoText
            text={t('activityLogs.emptyState')}
            className="!text-base !font-semibold text-light-text-primary dark:text-dark-text-primary"
            data-testid="activity-logs-list-empty-title"
          />
          <InfoText
            text={t('activityLogs.emptyStateDesc')}
            className="!text-sm text-light-text-secondary dark:text-dark-text-secondary mt-1"
            data-testid="activity-logs-list-empty-desc"
          />
        </div>
      );
    }
    return null;
  }

  return (
    <div
      className={cn('flex flex-col space-y-4 w-full', className)}
      data-testid="activity-logs-list-container"
    >
      {!hideTitle && (
        <InfoText
          text={t('activityLogs.logActivity')}
          className="!text-lg !font-bold text-light-text-primary dark:text-dark-text-primary"
          data-testid="activity-logs-list-title"
        />
      )}

      <div className="space-y-4">
        {logsList?.map((logItem) => (
          <div
            key={logItem.date}
            className="border-b border-gray-200 pb-4 last:border-b-0 last:pb-0"
          >
            <IndividualLogItem
              title={logItem.action?.name || '-'}
              user={logItem.user}
              msg={logItem.message || '-'}
              date={logItem.date || '-'}
            />
          </div>
        ))}
      </div>
    </div>
  );
};

export default ActivityLogsList;
